import { useEffect, useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faCheckCircle, faClose, faUserPlus, faTag, faCreditCard, faStar, faMoneyBill, faFileInvoiceDollar, faCodeBranch, faCoins, faChevronDown, faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import { eventBus } from '../../bus/eventBus';
import PatientPickerDropdown from '../Patient/PatientPickerDropdown';
import Form from '../Form/Form';
import Select from '../Form/Fields/Select';
import Switcher from '../Form/Fields/Switcher';
import Radio from '../Form/Fields/Radio';
import Button from '../Form/Button';
import InputText from '../Form/Fields/InputText';
import { toast } from 'react-toastify';
import axios from 'axios';
import CreateProductModal from '../Modal/CreateProductModal';
import BarcodeScanner from '../BarcodeScanner';
import { useAuth } from '../../context/AuthProvider';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';

import './Cart.scss';
import { formatPrice } from '../../utils/FormatPrice';
import { formatExpirationDate } from '../../utils/FormatDate';
import { PaymentMethod } from '../../config/PaymentMethod';
import { API_BASE_URL } from '../../api';
import useOnlineStatus from '../../offline/useOnlineStatus';
import { lookupByBarcode } from '../../offline/productCache';
import { enqueueOrder } from '../../offline/pendingOrders';
import { toastCreated } from '../../utils/ToastLink';

const PAYMENT_METHOD_ICONS = {
  CASH: faMoneyBill,
  SPLIT_PAYMENT: faCodeBranch,
  CHECKS_PAYMENT: faFileInvoiceDollar,
  CARD: faCreditCard,
};

// Matches Table.js's default dragMimeType — a product row dragged out of a listing
// (e.g. /products) carries its payload under this key.
const PRODUCT_DRAG_MIME = 'application/x-saydalani-row';

const Cart = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isOnline } = useOnlineStatus();
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [items, setItems] = useState([]);
  const [reduction, setReduction] = useState('');
  const [isPercentage, setIsPercentage] = useState(false);
  const [code, setCode] = useState('');
  const cartRef = useRef(null);
  const itemsRef = useRef(null);
  const currentPatientIdRef = useRef(null);
  const patientPopoverRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState(null); // 'patient' | 'discount' | 'payment' | null
  const [formKey, setFormKey] = useState(0);

  // Patient popover closes on an outside click, like any other anchored dropdown.
  useEffect(() => {
    if (activeSection !== 'patient') return;
    const handleClickOutside = (e) => {
      if (patientPopoverRef.current && !patientPopoverRef.current.contains(e.target)) {
        setActiveSection(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeSection]);
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [scannedBarcode, setScannedBarcode] = useState('');
  const [isSearchingProduct, setIsSearchingProduct] = useState(false);
  const [newItemIndex, setNewItemIndex] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [highlightAmount, setHighlightAmount] = useState(false);
  const paymentMethods = PaymentMethod;
  const [amountToPay, setAmountToPay] = useState(0);
  const [cashGiven, setCashGiven] = useState('');
  const [cashCurrency, setCashCurrency] = useState('DH');
  const [exchangeRate, setExchangeRate] = useState('');
  const [otherCurrencyCode, setOtherCurrencyCode] = useState('');
  const [rateLoading, setRateLoading] = useState(false);
  const [rateError, setRateError] = useState(null);
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
  const currencyDropdownRef = useRef(null);
  const [editingPriceIndex, setEditingPriceIndex] = useState(null);
  const [editingPriceValue, setEditingPriceValue] = useState('');

  // Currency dropdown closes on an outside click, like the patient popover.
  useEffect(() => {
    if (!currencyDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(e.target)) {
        setCurrencyDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [currencyDropdownOpen]);

  // Loyalty
  const { settings } = useAuth();
  const cartAutoOpen = settings['sales_inventory_cart_auto_open'] !== false;
  const cartPrioritizeOldestBatch = settings['sales_inventory_cart_prioritize_oldest_batch'] !== false;
  const batchExpirationStrategy = cartPrioritizeOldestBatch ? 'soon_expired' : 'latest_expired';
  const cartShowBatchSelection = settings['sales_inventory_cart_show_batch_selection'] !== false;
  const cartMergeQuantities = !!settings['sales_inventory_cart_merge_quantities'];
  const requirePatientForSplitPayment = !!settings['sales_inventory_require_patient_for_split_payment'];
  const loyaltyEnabled = !!settings['loyalty_general_enabled'];
  const redeemRatio = parseFloat(settings['loyalty_redeem_ratio'] || 1);
  const earnPoints = parseFloat(settings['loyalty_earn_points'] || 1);
  const earnPer = parseFloat(settings['loyalty_earn_per'] || 1);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [selectedPatientName, setSelectedPatientName] = useState(null);
  const [loyaltyCard, setLoyaltyCard] = useState(null);
  const [applyLoyalty, setApplyLoyalty] = useState(false);

  useEffect(() => {
    if (!selectedPatientId || !loyaltyEnabled) {
      setLoyaltyCard(null);
      setApplyLoyalty(false);
      return;
    }
    axios.get(`${API_BASE_URL}/api/v1/loyalty-cards/patient/${selectedPatientId}`, { withCredentials: true })
      .then(({ data }) => setLoyaltyCard(data))
      .catch((err) => {
        // 404 = patient has no loyalty card, not an error
        if (err?.response?.status !== 404) console.error(err);
        setLoyaltyCard(null);
      });
  }, [selectedPatientId, loyaltyEnabled]);

  // Cash received in a foreign currency — the DH rate is always fetched automatically
  // from the conversion API configured in Ventes > Taux de change, never typed manually.
  useEffect(() => {
    if (cashCurrency === 'DH') {
      setExchangeRate('');
      setRateError(null);
      return;
    }
    const code = cashCurrency === 'OTHER' ? otherCurrencyCode.trim().toUpperCase() : cashCurrency;
    if (!code || (cashCurrency === 'OTHER' && code.length !== 3)) {
      setExchangeRate('');
      setRateError(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      setRateLoading(true);
      setRateError(null);
      axios.get(`${API_BASE_URL}/api/v1/currency/rate`, { params: { currency: code }, withCredentials: true })
        .then(({ data }) => { if (!cancelled) setExchangeRate(String(data.rate)); })
        .catch(() => {
          if (!cancelled) {
            setExchangeRate('');
            setRateError('Taux indisponible pour cette devise');
          }
        })
        .finally(() => { if (!cancelled) setRateLoading(false); });
    }, cashCurrency === 'OTHER' ? 500 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [cashCurrency, otherCurrencyCode]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isExpiredBatch = (batch) => batch.expirationDate && new Date(batch.expirationDate) < today;

  const getBatchByStrategy = (batches = [], strategy = 'soon_expired') => {
    const availableBatches = batches.filter(b => b.quantity > 0 && !isExpiredBatch(b));
    if (!availableBatches.length) return null;
    const sorted = [...availableBatches].sort((a, b) => new Date(a.expirationDate) - new Date(b.expirationDate));
    return strategy === 'latest_expired' ? sorted[sorted.length - 1] : sorted[0];
  };

  const fetchOrderableForCart = async (barcode) => {
    if (!isOnline) return lookupByBarcode(barcode);
    const { data } = await axios.get(`${API_BASE_URL}/api/v1/cart/barcode/${barcode}`, { withCredentials: true });
    return data;
  };

  const handleDetected = async (barcode) => {
    setCode(barcode);
    setIsSearchingProduct(true);
    try {
      const data = await fetchOrderableForCart(barcode);
      if (!data.found) {
        if (!isOnline) {
          toast.error("Produit introuvable dans le cache hors-ligne. Impossible d'ajouter un nouveau produit sans connexion.");
        } else {
          setScannedBarcode(barcode);
          setShowCreateProductModal(true);
        }
      } else {
        eventBus.emit('newCartItem', data);
      }
    } catch (error) {
      toast.error('Erreur lors de la recherche du produit');
    } finally {
      setIsSearchingProduct(false);
    }
  };

  // Drag-and-drop a product row (e.g. from /products) onto the cart trigger/panel.
  const [isDropTarget, setIsDropTarget] = useState(false);
  const [isProductDragging, setIsProductDragging] = useState(false);
  const dragCounterRef = useRef(0);

  // The trigger button should start blinking the moment a product row starts being
  // dragged anywhere on the page — not only once the cursor is already hovering it —
  // so the user notices where to drop it. dragstart/dragend bubble to the document.
  useEffect(() => {
    const onDocDragStart = (e) => {
      if (e.dataTransfer?.types?.includes(PRODUCT_DRAG_MIME)) setIsProductDragging(true);
    };
    const onDocDragEnd = () => {
      setIsProductDragging(false);
      setIsDropTarget(false);
      dragCounterRef.current = 0;
    };
    document.addEventListener('dragstart', onDocDragStart);
    document.addEventListener('dragend', onDocDragEnd);
    return () => {
      document.removeEventListener('dragstart', onDocDragStart);
      document.removeEventListener('dragend', onDocDragEnd);
    };
  }, []);

  const addProductToCart = async (productId) => {
    await addOrderableToCart('product', productId);
  };

  const addOrderableToCart = async (type, id) => {
    setIsSearchingProduct(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/v1/cart/${type}/${id}`, { withCredentials: true });
      eventBus.emit('newCartItem', data);
    } catch (error) {
      toast.error(t('product.error_add_to_cart'));
    } finally {
      setIsSearchingProduct(false);
    }
  };

  const handleDragEnter = (e) => {
    if (!e.dataTransfer.types.includes(PRODUCT_DRAG_MIME)) return;
    e.preventDefault();
    dragCounterRef.current += 1;
    setIsDropTarget(true);
  };

  const handleDragLeave = (e) => {
    if (!e.dataTransfer.types.includes(PRODUCT_DRAG_MIME)) return;
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDropTarget(false);
  };

  const handleDragOver = (e) => {
    if (!e.dataTransfer.types.includes(PRODUCT_DRAG_MIME)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleProductDrop = (e) => {
    const raw = e.dataTransfer.getData(PRODUCT_DRAG_MIME);
    if (!raw) return;
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDropTarget(false);
    let payload;
    try { payload = JSON.parse(raw); } catch { return; }
    if ((payload?.type === 'product' || payload?.type === 'service') && payload.id) {
      addOrderableToCart(payload.type, payload.id);
    }
  };

  BarcodeScanner({
    onComplete: (code) => handleDetected(code),
    onError: (code) => console.warn('scan error :', code),
    minLength: 5,
  });

  useEffect(() => {
    const handleNewCartItem = (item) => {
      const selectedBatch = getBatchByStrategy(item.batches, batchExpirationStrategy);
      const selectedBatchId = selectedBatch?.id || null;
      let merged = false;
      setItems(prev => {
        if (cartMergeQuantities) {
          const existingIndex = prev.findIndex(
            it => it.id === item.id && it.type === item.type && it.selectedBatchId === selectedBatchId
          );
          if (existingIndex !== -1) {
            merged = true;
            const next = prev.map((it, i) => i === existingIndex ? { ...it, quantity: (it.quantity || 1) + 1 } : it);
            setNewItemIndex(existingIndex);
            setTimeout(() => setNewItemIndex(null), 1800);
            return next;
          }
        }
        const next = [...prev, { ...item, selectedBatchId, quantity: 1 }];
        setNewItemIndex(next.length - 1);
        setTimeout(() => setNewItemIndex(null), 1800);
        return next;
      });
      if (cartAutoOpen) openOffcanvas();
      else toast.success(merged ? `${item.name} — quantité mise à jour` : `${item.name} ajouté au panier`);
    };

    eventBus.on('newCartItem', handleNewCartItem);
    return () => eventBus.off('newCartItem', handleNewCartItem);
  }, [cartAutoOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!document.contains(event.target)) return;
      if (cartRef.current && !cartRef.current.contains(event.target)) {
        if (event.target.closest('.dropdown-menu')) return;
        closeOffcanvas();
      }
    };
    if (isVisible) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isVisible]);

  useEffect(() => {
    if (itemsRef.current) {
      itemsRef.current.scrollTop = itemsRef.current.scrollHeight;
    }
  }, [items.length]);

  const openOffcanvas = () => { setIsVisible(true); setTimeout(() => setIsAnimating(true), 10); };
  const closeOffcanvas = () => { setIsAnimating(false); setTimeout(() => setIsVisible(false), 300); };

  const getTotal = () => items.reduce((total, item) => total + (isNaN(item?.price) ? 0 : item.price) * (item?.quantity || 1), 0);
  const totalUnits = items.reduce((n, item) => n + (item?.quantity || 1), 0);
  const getReductionAmount = (total) => {
    const red = parseFloat(reduction);
    if (isNaN(red)) return 0;
    return isPercentage ? (total * Math.min(red, 100) / 100) : red;
  };

  const total = getTotal();
  const reductionAmount = getReductionAmount(total);
  const afterManualDiscount = Math.max(0, total - reductionAmount);

  // Loyalty discount — can't exceed the after-manual-discount amount
  const loyaltyPoints = loyaltyCard?.currentPoints ?? 0;
  const maxLoyaltyValue = loyaltyPoints * redeemRatio;
  const loyaltyDiscountValue = applyLoyalty && loyaltyPoints > 0
    ? Math.min(maxLoyaltyValue, afterManualDiscount)
    : 0;
  const loyaltyPointsUsed = loyaltyDiscountValue > 0 ? Math.ceil(loyaltyDiscountValue / redeemRatio) : 0;

  const subtotal = Math.max(0, total).toFixed(2);
  const realTotal = afterManualDiscount - loyaltyDiscountValue;
  const finalTotal = Math.max(0, paymentMethod === 'SPLIT_PAYMENT' && amountToPay >= 0 ? amountToPay : realTotal).toFixed(2);
  const amountDue = realTotal - amountToPay;

  const cashGivenNum = parseFloat(cashGiven);
  const exchangeRateNum = cashCurrency === 'DH' ? 1 : parseFloat(exchangeRate);
  const cashGivenInDh = !isNaN(cashGivenNum) && !isNaN(exchangeRateNum) ? cashGivenNum * exchangeRateNum : NaN;
  const change = paymentMethod === 'CASH' && cashGiven !== '' && !isNaN(cashGivenInDh)
    ? cashGivenInDh - parseFloat(finalTotal)
    : null;

  const handleCurrencyChange = (currency) => {
    setCashCurrency(currency);
    setCashGiven('');
    if (currency !== 'OTHER') setOtherCurrencyCode('');
    if (currency !== 'OTHER') setCurrencyDropdownOpen(false);
  };

  const cashPresets = (() => {
    const t = parseFloat(finalTotal);
    if (!t || isNaN(t)) return [];
    const results = [];
    const near10  = Math.ceil(t / 10)  * 10;
    const near50  = Math.ceil(t / 50)  * 50;
    const near100 = Math.ceil(t / 100) * 100;
    const near200 = Math.ceil(t / 200) * 200;
    if (near10  > t)       results.push(near10);
    if (near50  > near10)  results.push(near50);
    if (near100 > near50)  results.push(near100);
    if (near200 > near100) results.push(near200);
    return results.slice(0, 3);
  })();

  const handleBatchChange = (itemIndex, batchId) => {
    setItems(prev => prev.map((item, i) => i === itemIndex ? { ...item, selectedBatchId: batchId } : item));
  };

  const handleQtyStepChange = (itemIndex, delta) => {
    setItems(prev => prev
      .map((item, i) => i === itemIndex ? { ...item, quantity: (item.quantity || 1) + delta } : item)
      .filter(item => (item.quantity || 1) > 0)
    );
  };

  const handlePriceEditStart = (index) => {
    setEditingPriceIndex(index);
    setEditingPriceValue(String(items[index].price));
  };

  const handlePriceEditCommit = async (index) => {
    const newPrice = parseFloat(editingPriceValue);
    if (!isNaN(newPrice) && newPrice >= 0) {
      const item = items[index];
      setItems(prev => prev.map((it, i) => i === index ? { ...it, price: newPrice } : it));
      if (item.type === 'product') {
        try {
          await axios.patch(
            `${API_BASE_URL}/api/v1/products/${item.id}`,
            { publicPrice: newPrice },
            { withCredentials: true }
          );
        } catch (err) {
          console.error('Failed to save price override', err);
        }
      }
    }
    setEditingPriceIndex(null);
  };

  const handleCollect = async (formData) => {
    if (requirePatientForSplitPayment && paymentMethod === 'SPLIT_PAYMENT' && !formData.patientId) {
      toast.error(t('A patient is required for credit payment'));
      setActiveSection('patient');
      return;
    }
    setLoading(true);
    const requestData = {
      // Expanded to one entry per unit — the backend order-creation contract has no notion of a
      // per-line quantity, quantity here is a purely cart-side (UI) way to avoid duplicate rows.
      itemsWithBatches: items.flatMap(item =>
        Array.from({ length: item.quantity || 1 }, () => ({ itemType: item.type, itemId: item.id, batchId: item.selectedBatchId }))
      ),
      discount: reduction ? { type: isPercentage ? 'PERCENT' : 'AMOUNT', value: reduction } : null,
      loyaltyDiscount: loyaltyPointsUsed > 0 ? { points: loyaltyPointsUsed, amount: loyaltyDiscountValue } : null,
      patientId: formData.patientId,
      payment: {
        method: paymentMethod,
        amountToPay,
        cashGiven: paymentMethod === 'CASH' && cashGiven !== '' && !isNaN(cashGivenInDh) ? cashGivenInDh : null,
      },
      subtotal,
      total: finalTotal
    };

    const resetCartUI = () => {
      setItems([]);
      setReduction('');
      setIsPercentage(false);
      setActiveSection(null);
      setSelectedPatientId(null);
      setSelectedPatientName(null);
      currentPatientIdRef.current = null;
      setLoyaltyCard(null);
      setApplyLoyalty(false);
      setPaymentMethod('CASH');
      setCashGiven('');
      setCashCurrency('DH');
      setExchangeRate('');
      setOtherCurrencyCode('');
      setAmountToPay(0);
      setFormKey(k => k + 1);
      closeOffcanvas();
    };

    // No connection — queue the sale locally instead of failing outright.
    // Loyalty redeem/earn isn't replayed on sync, so it's skipped entirely
    // here rather than silently promising something that won't happen.
    if (!isOnline) {
      try {
        await enqueueOrder(requestData);
        resetCartUI();
        toast.info('Vente enregistrée hors-ligne — elle sera synchronisée automatiquement au retour du réseau.');
      } catch (error) {
        toast.error("Échec de l'enregistrement de la vente hors-ligne");
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/v1/createOrder`, requestData, { withCredentials: true });

      // Loyalty: redeem then earn
      if (formData.patientId && loyaltyEnabled && loyaltyCard) {
        try {
          if (loyaltyPointsUsed > 0) {
            await axios.post(
              `${API_BASE_URL}/api/v1/loyalty-cards/${loyaltyCard.id}/redeem`,
              { points: loyaltyPointsUsed },
              { withCredentials: true }
            );
          }
          const earnableAmount = parseFloat(finalTotal);
          if (earnableAmount >= earnPer) {
            await axios.post(
              `${API_BASE_URL}/api/v1/loyalty-cards/${loyaltyCard.id}/earn`,
              { orderAmount: earnableAmount, description: `Vente` },
              { withCredentials: true }
            );
          }
        } catch (_) {
          // loyalty errors are non-blocking
        }
      }

      resetCartUI();
      eventBus.emit('orderPlaced');
      toastCreated(navigate, t('Order successfully created !'), response.data?.id && `/order/${response.data.id}`, t('Click to view the order'));
      eventBus.emit('orderCreated', response.data);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Une erreur est survenue lors de la création de la vente');
    } finally {
      setLoading(false);
    }
  };

  return (
    // A plain wrapper, not a Fragment: the trigger button is followed by several
    // siblings that are always in the DOM regardless of open/closed state (the
    // cart Form, the beep <audio>, etc.) — as a Fragment, each of those becomes
    // its own flex item in the header's icon row, and the row's own `gap` gets
    // added after the button for every such invisible sibling, pushing the
    // visible circle further from the row's right edge than intended. Wrapping
    // them all in one block-level div makes that div the only real flex item.
    <div className="cart-root">
      <button
        type="button"
        className={`cart-trigger${items.length > 0 && !isVisible ? ' cart-trigger--has-items' : ''}${isSearchingProduct ? ' cart-trigger--loading' : ''}${isProductDragging ? ' cart-trigger--dragging' : ''}${isDropTarget ? ' cart-trigger--drop-target' : ''}`}
        onClick={openOffcanvas}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleProductDrop}
        data-tooltip-id="tooltip"
        data-tooltip-content={isSearchingProduct ? t('Searching for product...') : t('cart.open_tooltip')}
      >
        {isSearchingProduct ? (
          <span className="spinner-border spinner-border-sm text-light" />
        ) : (
          <FontAwesomeIcon icon={faShoppingCart} />
        )}
        {!isSearchingProduct && items.length > 0 && <span className="cart-trigger__badge">{totalUnits}</span>}
      </button>

      <Form key={formKey} id="cartForm" onSubmit={handleCollect}>
        {isVisible && (
          <div
            ref={cartRef}
            className={`cart-panel ${isAnimating ? 'cart-panel--open' : 'cart-panel--closed'}${isDropTarget ? ' cart-panel--drop-target' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleProductDrop}
          >

            {/* Header */}
            <div className="cart-header">
              <div className="cart-header__left">
                <div className="cart-header__icon">
                  <FontAwesomeIcon icon={faShoppingCart} />
                </div>
                <div>
                  <p className="cart-header__title">{t('Cart')}</p>
                  <p className="cart-header__subtitle">{totalUnits} {t('item')}{totalUnits !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <div className="cart-header__actions" ref={patientPopoverRef}>
                {selectedPatientName ? (
                  <button type="button" className="cart-header__btn cart-header__btn--patient" onClick={() => setActiveSection(s => s === 'patient' ? null : 'patient')}>
                    <FontAwesomeIcon icon={faUserPlus} />
                    <span className="cart-header__btn-patient-name">{selectedPatientName}</span>
                  </button>
                ) : (
                  <button type="button" className={`cart-header__btn ${activeSection === 'patient' ? 'cart-header__btn--active' : ''}`} onClick={() => setActiveSection(s => s === 'patient' ? null : 'patient')}>
                    <FontAwesomeIcon icon={faUserPlus} />
                    {t('Patient')}
                  </button>
                )}

                {/* Patient selector — popover anchored to the button, results shown right away */}
                {activeSection === 'patient' && (
                  <div className="cart-patient-popover">
                    <PatientPickerDropdown
                      name="patientId"
                      onSelect={(patient) => {
                        setSelectedPatientId(patient.id);
                        setSelectedPatientName(patient.fullName);
                        currentPatientIdRef.current = patient.id ?? null;
                        setActiveSection(null);
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Scanning indicator */}
            {isSearchingProduct && (
              <div className="cart-scanning">
                <span className="spinner-border spinner-border-sm" />
                <span>{t('Searching for product...')}</span>
              </div>
            )}

            {/* Items */}
            <div
              className="cart-items"
              ref={itemsRef}
              onClick={() => setActiveSection(s => (s === 'discount' || s === 'payment') ? null : s)}
            >
              {items.length === 0 ? (
                <div className="cart-empty">
                  <FontAwesomeIcon icon={faShoppingCart} className="cart-empty__icon" />
                  <p className="cart-empty__title">{t('Empty cart')}</p>
                  <p className="cart-empty__subtitle">{t('Scan a product to get started')}</p>
                </div>
              ) : (
                items.map((item, index) => (
                  <div key={`${item.id}-${index}`} className={`cart-item${index === newItemIndex ? ' cart-item--new' : ''}`}>
                    {/* product picture (placeholder when it has none); services have no picture */}
                    {item.type === 'product' && (
                      <img className="cart-item__img" src={item.image || '/images/no_product.png'} alt="" />
                    )}
                    <div className="cart-item__info">
                      {item.type === 'product'
                        ? <NavLink to={`/products/${item.id}`} className="cart-item__name">{item.name}</NavLink>
                        : <p className="cart-item__name">{item.name}</p>
                      }
                      {item.description && <p className="cart-item__desc">{item.description}</p>}
                      {/* stock first (what matters at the counter), then refundable */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 9 }}>
                        {item?.hasStock && item?.stock <= 0 && (
                          <span className="cart-item__no-stock">{t('No stock available')}</span>
                        )}
                        {item?.hasStock && item?.stock > 0 && (
                          <span className="cart-item__stock-badge">{t('cart.stock_count', { count: item.stock })}</span>
                        )}
                        {/* only when refundable: "Non remboursable" was on most lines and said little */}
                        {item.type === 'product' && item.refundable && (
                          <span className="cart-item__refund-badge cart-item__refund-badge--yes">
                            {t('Refundable')}
                          </span>
                        )}
                        {item?.stock > 0 && (() => {
                          const batches = item.batches || [];
                          const hasValidBatch = batches.some(b => b.quantity > 0 && !isExpiredBatch(b));
                          const hasExpiredBatch = batches.some(b => b.quantity > 0 && isExpiredBatch(b));
                          return !hasValidBatch && hasExpiredBatch
                            ? <span className="cart-item__no-stock">Lots périmés</span>
                            : null;
                        })()}
                      </div>
                      {item?.stock > 0 && cartShowBatchSelection && (() => {
                        const validBatches = (item.batches || []).filter(b => b.quantity > 0 && !isExpiredBatch(b));
                        if (validBatches.length <= 1) return null;
                        return (
                          <div className="cart-item__batches">
                            {validBatches.map(batch => (
                              <Radio
                                key={batch.id}
                                name={`batch-${item.id}-${index}`}
                                value={batch.id}
                                label={batch.expirationDate
                                  ? formatExpirationDate(batch.expirationDate)
                                  : batch.batchNumber}
                                validationRules={{ required: 'Batch is required' }}
                                onChange={() => handleBatchChange(index, batch.id)}
                                checked={item.selectedBatchId === batch.id}
                              />
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                    <div className={`cart-item__right${cartMergeQuantities ? ' cart-item__right--qty' : ''}`}>
                      {cartMergeQuantities && (
                        <>
                          <button
                            type="button"
                            className="cart-item__remove cart-item__remove--corner"
                            onClick={() => setItems(prev => prev.filter((_, i) => i !== index))}
                            aria-label="Retirer du panier"
                          >
                            <FontAwesomeIcon icon={faClose} />
                          </button>
                          <div className="cart-item__qty-stepper">
                            <button type="button" onClick={() => handleQtyStepChange(index, -1)} aria-label="Diminuer la quantité">−</button>
                            <span className="cart-item__qty-value">{item.quantity || 1}</span>
                            <button type="button" onClick={() => handleQtyStepChange(index, 1)} aria-label="Augmenter la quantité">+</button>
                          </div>
                        </>
                      )}

                      {cartMergeQuantities ? (
                        <div className="cart-item__price-block">
                          {/* a single unit: the line total is the unit price, so it's the one to click
                              to edit it (the "price × qty" line below only shows from 2 units) */}
                          {(item.quantity || 1) === 1 && editingPriceIndex === index ? (
                            <input
                              type="number"
                              className="cart-item__price-input"
                              value={editingPriceValue}
                              autoFocus
                              min={0}
                              step="0.01"
                              onChange={e => setEditingPriceValue(e.target.value)}
                              onBlur={() => handlePriceEditCommit(index)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') { e.preventDefault(); handlePriceEditCommit(index); }
                                if (e.key === 'Escape') setEditingPriceIndex(null);
                              }}
                            />
                          ) : (
                          <span
                            className={`cart-item__line-total-main${(item.quantity || 1) === 1 && item.type === 'product' ? ' cart-item__price--editable' : ''}`}
                            onClick={() => (item.quantity || 1) === 1 && item.type === 'product' && handlePriceEditStart(index)}
                          >
                            {new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(item.price * (item.quantity || 1))}
                            <span className="cart-item__price-unit-label"> dhs</span>
                          </span>
                          )}
                          {(item.quantity || 1) > 1 && (
                            editingPriceIndex === index ? (
                              <input
                                type="number"
                                className="cart-item__price-input cart-item__price-input--sub"
                                value={editingPriceValue}
                                autoFocus
                                min={0}
                                step="0.01"
                                onChange={e => setEditingPriceValue(e.target.value)}
                                onBlur={() => handlePriceEditCommit(index)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') { e.preventDefault(); handlePriceEditCommit(index); }
                                  if (e.key === 'Escape') setEditingPriceIndex(null);
                                }}
                              />
                            ) : (
                              <span
                                className={`cart-item__unit-price-sub${item.type === 'product' ? ' cart-item__unit-price-sub--editable' : ''}`}
                                onClick={() => item.type === 'product' && handlePriceEditStart(index)}
                              >
                                {new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(item.price)} dhs × {item.quantity}
                              </span>
                            )
                          )}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                          {editingPriceIndex === index ? (
                            <input
                              type="number"
                              className="cart-item__price-input"
                              value={editingPriceValue}
                              autoFocus
                              min={0}
                              step="0.01"
                              onChange={e => setEditingPriceValue(e.target.value)}
                              onBlur={() => handlePriceEditCommit(index)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') { e.preventDefault(); handlePriceEditCommit(index); }
                                if (e.key === 'Escape') setEditingPriceIndex(null);
                              }}
                            />
                          ) : (
                            <span
                              className={`cart-item__price${item.type === 'product' ? ' cart-item__price--editable' : ''}`}
                              onClick={() => item.type === 'product' && handlePriceEditStart(index)}
                            >
                              {new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(item.price)}
                            </span>
                          )}
                          <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>dhs</span>
                        </div>
                      )}

                      {!cartMergeQuantities && (
                        <button type="button" className="cart-item__remove" onClick={() => setItems(prev => prev.filter((_, i) => i !== index))}>
                          <FontAwesomeIcon icon={faClose} />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Summary */}
            <div className="cart-summary">

              {/* Discount row — dimmed while unused so it doesn't compete for attention with
                  rows that matter on every sale (Paiement); back to full opacity once a
                  discount is actually applied, since it's now real info about the total. */}
              <div
                className={`cart-summary__row cart-summary__row--action${reductionAmount > 0 ? '' : ' cart-summary__row--dimmed'}`}
                onClick={() => setActiveSection(s => s === 'discount' ? null : 'discount')}
              >
                <div className="cart-summary__row-left">
                  <FontAwesomeIcon icon={faTag} className="cart-summary__icon" />
                  <span className="cart-summary__label">{t('Discount')}</span>
                </div>
                <div className="cart-summary__row-right">
                  {reductionAmount > 0 ? (
                    <span className="cart-summary__value cart-summary__value--discount">- {formatPrice(reductionAmount.toFixed(2))}</span>
                  ) : (
                    <span className="cart-summary__add cart-summary__add--muted">+ Add</span>
                  )}
                </div>
              </div>
              {activeSection === 'discount' && (
                <div className="cart-discount">
                  <div className="cart-discount__input-group">
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      placeholder={isPercentage ? t('Discount in %') : t('Discount in Dhs')}
                      max={isPercentage ? 100 : undefined}
                      min={0}
                      value={reduction}
                      onChange={e => {
                        const val = e.target.value;
                        if (isPercentage && val !== '' && parseFloat(val) > 100) {
                          setReduction('100');
                          return;
                        }
                        setReduction(val);
                      }}
                    />
                    <div className="cart-discount__toggle">
                      <span className="cart-discount__toggle-label">
                        {isPercentage ? '%' : 'Dhs'}
                      </span>
                      <Switcher
                        name="discountIsPercentage"
                        size="mini"
                        onChange={(val) => {
                          setIsPercentage(val);
                          if (val && parseFloat(reduction) > 100) {
                            setReduction('100');
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Loyalty points row — always visible */}
              {loyaltyEnabled && loyaltyCard && (
                <div
                  className={`cart-summary__row ${loyaltyPoints > 0 ? 'cart-summary__row--action' : ''}`}
                  onClick={() => loyaltyPoints > 0 && setApplyLoyalty(a => !a)}
                >
                  <div className="cart-summary__row-left">
                    <FontAwesomeIcon icon={faStar} className="cart-summary__icon cart-summary__icon--loyalty" />
                    <span className="cart-summary__label">
                      {t('Loyalty points')} <span className="cart-summary__badge cart-summary__badge--loyalty">{loyaltyPoints} {t('pts')}</span>
                    </span>
                  </div>
                  <div className="cart-summary__row-right">
                    {loyaltyPoints === 0 ? (
                      <span className="cart-summary__add" style={{ color: '#adb5bd' }}>{t('No points')}</span>
                    ) : applyLoyalty ? (
                      <span className="cart-summary__value cart-summary__value--discount">- {formatPrice(loyaltyDiscountValue.toFixed(2))}</span>
                    ) : (
                      <span className="cart-summary__add">{t('Use')} ({formatPrice(maxLoyaltyValue.toFixed(2))})</span>
                    )}
                  </div>
                </div>
              )}

              {/* Payment row */}
              <div className="cart-summary__row cart-summary__row--action" onClick={() => setActiveSection(s => s === 'payment' ? null : 'payment')}>
                <div className="cart-summary__row-left">
                  <FontAwesomeIcon icon={faCreditCard} className="cart-summary__icon" />
                  <span className="cart-summary__label">{t('Payment')}</span>
                </div>
                <div className="cart-summary__row-right">
                  <span className="cart-summary__add">{t('Change')}</span>
                  <span className="cart-summary__badge">
                    <FontAwesomeIcon icon={PAYMENT_METHOD_ICONS[paymentMethod]} />
                    {paymentMethods[paymentMethod]}
                  </span>
                </div>
              </div>
              {activeSection === 'payment' && (
                <div className="cart-payment">
                  <div className="cart-payment__methods">
                    {[
                      { key: 'CASH',           label: paymentMethods.CASH,           icon: PAYMENT_METHOD_ICONS.CASH },
                      { key: 'SPLIT_PAYMENT',  label: paymentMethods.SPLIT_PAYMENT,  icon: PAYMENT_METHOD_ICONS.SPLIT_PAYMENT },
                      { key: 'CHECKS_PAYMENT', label: paymentMethods.CHECKS_PAYMENT, icon: PAYMENT_METHOD_ICONS.CHECKS_PAYMENT },
                      { key: 'CARD',           label: paymentMethods.CARD,           icon: PAYMENT_METHOD_ICONS.CARD },
                    ].map(({ key, label, icon }) => (
                      <button
                        key={key}
                        type="button"
                        className={`cart-payment__method-btn${paymentMethod === key ? ' cart-payment__method-btn--active' : ''}`}
                        onClick={() => {
                          setPaymentMethod(key);
                          setCashGiven('');
                          setCashCurrency('DH');
                          setExchangeRate('');
                          setOtherCurrencyCode('');
                          setActiveSection(null);
                          // Draws the eye to the amount field right after picking a method,
                          // so the pharmacist doesn't have to hunt for what to fill in next.
                          setHighlightAmount(true);
                          setTimeout(() => setHighlightAmount(false), 1000);
                        }}
                      >
                        <FontAwesomeIcon icon={icon} className="cart-payment__method-icon" />
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Split payment amount */}
              {paymentMethod === 'SPLIT_PAYMENT' && (
                <div className={`cart-cash${highlightAmount ? ' cart-cash--highlight' : ''}`}>
                  <div className="cart-cash__header">
                    <span className="cart-cash__label">
                      Montant à payer
                      <FontAwesomeIcon
                        icon={faCircleQuestion}
                        className="cart-cash__help-icon"
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Saisissez le montant que le client vous a donné pour ce premier paiement"
                      />
                    </span>
                  </div>
                  <input
                    type="number"
                    className="cart-cash__input"
                    placeholder={`Max. ${realTotal.toFixed(2)} dhs`}
                    value={amountToPay || ''}
                    min={0}
                    max={realTotal}
                    step="0.01"
                    onChange={e => setAmountToPay(Math.min(parseFloat(e.target.value) || 0, realTotal))}
                  />
                  {amountToPay > 0 && (
                    <div className={`cart-cash__change ${amountDue < 0 ? 'cart-cash__change--insufficient' : ''}`}>
                      {amountDue < 0
                        ? <>Dépassement de <strong>{formatPrice(Math.abs(amountDue).toFixed(2))}</strong></>
                        : <>Reste dû <strong>{formatPrice(amountDue.toFixed(2))}</strong></>
                      }
                    </div>
                  )}
                </div>
              )}

              {/* Cash given — only for CASH payments with items */}
              {paymentMethod === 'CASH' && items.length > 0 && (
                <div className={`cart-cash${highlightAmount ? ' cart-cash--highlight' : ''}`}>
                  <div className="cart-cash__header">
                    <span className="cart-cash__label">Montant reçu</span>
                    {cashCurrency === 'DH' && cashPresets.length > 0 && (
                      <div className="cart-cash__presets">
                        {cashPresets.map(p => (
                          <button key={p} type="button" className="cart-cash__preset" onClick={() => setCashGiven(String(p))}>
                            {p} dhs
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="cart-cash__input-wrap">
                    <div className="cart-cash__currency-toggle-wrap" ref={currencyDropdownRef}>
                      <button
                        type="button"
                        className="cart-cash__currency-toggle"
                        onClick={() => setCurrencyDropdownOpen(o => !o)}
                      >
                        <FontAwesomeIcon icon={faCoins} className="cart-cash__currency-toggle-icon" />
                        <span>{cashCurrency === 'OTHER' ? (otherCurrencyCode || '???') : cashCurrency}</span>
                        <FontAwesomeIcon icon={faChevronDown} className="cart-cash__currency-toggle-caret" />
                      </button>

                      {currencyDropdownOpen && (
                        <div className="cart-cash__currency-dropdown">
                          {['DH', 'EUR', 'USD'].map(c => (
                            <button
                              key={c}
                              type="button"
                              className={`cart-cash__currency-option${cashCurrency === c ? ' cart-cash__currency-option--active' : ''}`}
                              onClick={() => handleCurrencyChange(c)}
                            >
                              {c}
                            </button>
                          ))}
                          <div className={`cart-cash__currency-option cart-cash__currency-option--custom${cashCurrency === 'OTHER' ? ' cart-cash__currency-option--active' : ''}`}>
                            <input
                              type="text"
                              placeholder="Autre (ex: GBP)"
                              value={cashCurrency === 'OTHER' ? otherCurrencyCode : ''}
                              maxLength={3}
                              autoFocus={cashCurrency === 'OTHER'}
                              onFocus={() => { if (cashCurrency !== 'OTHER') handleCurrencyChange('OTHER'); }}
                              onChange={e => {
                                if (cashCurrency !== 'OTHER') { setCashCurrency('OTHER'); setCashGiven(''); }
                                setOtherCurrencyCode(e.target.value.toUpperCase());
                              }}
                              onKeyDown={e => { if (e.key === 'Enter') setCurrencyDropdownOpen(false); }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <input
                      type="number"
                      className="cart-cash__input cart-cash__input--with-currency"
                      disabled={cashCurrency !== 'DH' && (!exchangeRate || isNaN(parseFloat(exchangeRate)))}
                      placeholder={cashCurrency === 'DH' ? `Min. ${finalTotal} dhs` : `Montant en ${cashCurrency === 'OTHER' ? (otherCurrencyCode || 'devise') : cashCurrency}`}
                      value={cashGiven}
                      min={0}
                      step="0.01"
                      onChange={e => setCashGiven(e.target.value)}
                    />
                  </div>

                  {cashCurrency !== 'DH' && (
                    rateLoading ? (
                      <div className="cart-cash__rate-status">Récupération du taux...</div>
                    ) : rateError ? (
                      <div className="cart-cash__rate-status cart-cash__rate-status--error">{rateError}</div>
                    ) : exchangeRate ? (
                      <div className="cart-cash__rate-status">
                        1 {cashCurrency === 'OTHER' ? otherCurrencyCode : cashCurrency} = {parseFloat(exchangeRate).toFixed(4)} dhs
                        {cashGiven !== '' && !isNaN(cashGivenInDh) && <> — {formatPrice(cashGivenInDh.toFixed(2))}</>}
                      </div>
                    ) : cashCurrency === 'OTHER' ? (
                      <div className="cart-cash__rate-status">Entrez un code devise à 3 lettres (ex: GBP)</div>
                    ) : null
                  )}

                  {change !== null && (
                    <div className={`cart-cash__change ${change < 0 ? 'cart-cash__change--insufficient' : ''}`}>
                      {change < 0
                        ? <>Insuffisant — manque <strong>{formatPrice(Math.abs(change).toFixed(2))}</strong></>
                        : <>Rendu monnaie <strong>{formatPrice(change.toFixed(2))}</strong></>
                      }
                    </div>
                  )}
                </div>
              )}

              {/* Totals */}
              <div className="cart-totals">
                <div className="cart-totals__line">
                  <span>{t('Subtotal')}</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                {reductionAmount > 0 && (
                  <div className="cart-totals__line cart-totals__line--discount">
                    <span>{t('Discount')}</span>
                    <span>- {formatPrice(reductionAmount.toFixed(2))}</span>
                  </div>
                )}
                {loyaltyDiscountValue > 0 && (
                  <div className="cart-totals__line cart-totals__line--discount">
                    <span>{t('Loyalty points')} ({loyaltyPointsUsed} {t('pts')})</span>
                    <span>- {formatPrice(loyaltyDiscountValue.toFixed(2))}</span>
                  </div>
                )}
                {paymentMethod === 'SPLIT_PAYMENT' && amountToPay > 0 && (
                  <div className="cart-totals__line cart-totals__line--due">
                    <span>{t('Amount due')}</span>
                    <span>{formatPrice(amountDue.toFixed(2))}</span>
                  </div>
                )}
                <div className="cart-totals__total">
                  <span>{t('Total')}</span>
                  <span>{formatPrice(finalTotal)}</span>
                </div>
              </div>

              {/* Collect button */}
              <div className="cart-footer">
              <Button
                isLoading={loading}
                loadingMessage={t('Processing...')}
                type="submit"
                className="cart-collect"
                disabled={items.length === 0 || (requirePatientForSplitPayment && paymentMethod === 'SPLIT_PAYMENT' && !selectedPatientId)}
              >
                <FontAwesomeIcon icon={faCheckCircle} />
                {t('Collect')} — {formatPrice(finalTotal)}
              </Button>
            </div>
            </div>

            {/* Close button */}
            <button type="button" onClick={closeOffcanvas} className="cart-close">
              <FontAwesomeIcon icon={faClose} />
            </button>
          </div>
        )}
      </Form>

      {showCreateProductModal && (
        <CreateProductModal
          show={showCreateProductModal}
          onClose={() => setShowCreateProductModal(false)}
          defaultValues={{ barcode: scannedBarcode, stockRequest: { quantity: 1 } }}
          onSave={(newProduct) => { setItems(prev => [...prev, newProduct.product]); setShowCreateProductModal(false); }}
        />
      )}

      <audio id="beep-audio" src="/beep.mp3" preload="auto" />
    </div>
  );
};

export default Cart;
